<template>
  <section :class="$style.sectionRoot">
    <header :class="[$style.sectionHeader]">
      Custom Attributes
    </header>
    <div :class="$style.sectionContent">
      <KnowledgeBaseArticle
        :class="{ [$style.kbArticle]: getUserAttributes.length }"
        :includeInfoIcon="false"
        :infoText="$options.learnMore.infoText"
        :kbLink="$options.learnMore.kbLink"
        :kbText="$options.learnMore.kbText"
      />
      <ListEditor
        v-if="getStringUserAttributes.length"
        buttonText=""
        :class="$style.listEditor"
        component="CustomAttributeFields"
        :handleChangeListItem="handleUpdateAttribute"
        :handleRemoveListItem="handleRemoveAttribute"
        headerText="Strings"
        :listItems="getStringUserAttributes"
        nameTitle="Attribute Name"
        nameValidation="required|reservedNameValidation|uniqueNameValidation"
        valueDynamicComponent="InputValidator"
        valueTitle="Attribute Value"
        valueValidation=""
      />
      <ListEditor
        v-if="getNumberUserAttributes.length"
        buttonText=""
        :class="$style.listEditor"
        component="CustomAttributeFields"
        :handleChangeListItem="handleUpdateAttribute"
        :handleRemoveListItem="handleRemoveAttribute"
        headerText="Numbers"
        :listItems="getNumberUserAttributes"
        nameTitle="Attribute Name"
        nameValidation="required|reservedNameValidation|uniqueNameValidation"
        valueDynamicComponent="InputValidator"
        valueMaxLengthValidation="9"
        valueTitle="Attribute Value"
        valueValidation="max:9|required|numeric"
      />
      <ListEditor
        v-if="getBooleanUserAttributes.length"
        buttonText=""
        :class="$style.listEditor"
        component="CustomAttributeFields"
        :handleChangeListItem="handleUpdateAttribute"
        :handleRemoveListItem="handleRemoveAttribute"
        headerText="Booleans"
        :listItems="getBooleanUserAttributes"
        nameTitle="Attribute Name"
        nameValidation="required|reservedNameValidation|uniqueNameValidation"
        valueDynamicComponent="BooleanAttributeInput"
        valueTitle="Attribute Value"
        valueValidation=""
      />
      <ListEditor
        v-if="getObjectUserAttributes.length"
        buttonText=""
        :class="$style.listEditor"
        component="CustomAttributeFields"
        :handleChangeListItem="handleUpdateAttribute"
        :handleRemoveListItem="handleRemoveAttribute"
        headerText="JSON Editor"
        :listItems="getObjectUserAttributes"
        nameTitle="Attribute Name"
        nameValidation="required|reservedNameValidation|uniqueNameValidation"
        :valueDynamicComponentProps="{ resizeable: true }"
        valueDynamicComponent="TextareaValidator"
        valuePlaceholder="JSON"
        valueTitle="Attribute Value"
        valueValidation="required|jsonFormatValidation"
      />
    </div>
    <DropDown
      v-validate="'required'"
      attribute="attributeType"
      :class="[$style.dropdownContainer, {
        [$style.noAttributes]: !getUserAttributes.length
      }]"
      headerText="Select Type"
      name="dropdownAttributeType"
      :options="$options.attributes"
      value="add new custom attribute"
      @input="(attributeType) => addAttribute({ attributeType })"
    />
  </section>
</template>

<script>
import { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import { cloneDeep } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import DropDown from 'client/components/FormValidation/DropDown.vue';
import GroupsConstants from 'client/util/Constants/GroupsConstants';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import KnowledgeBaseArticle from 'client/components/FormValidation/KnowledgeBaseArticle.vue';
import ListEditor from 'client/components/FormValidation/ListEditor';

const { attributesType, reservedGroupAttributes } = GroupsConstants;
const userGroupModel = StoreModelMap[ItemTypeConsts.userGroup];

export default {
  name: 'UserGroupCustomUserAttributes',

  components: {
    DropDown,
    KnowledgeBaseArticle,
    ListEditor,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
  ],

  inject: ['$validator'],

  attributes: Object.values(attributesType),

  learnMore: {
    infoText: 'Create and store custom attributes for users in this user group. Attribute field names must be unique. Conflicting attributes set at the user level will override attributes set at the group level. These attributes can be used on SSO Applications.',
    kbText: 'Learn more about how to use group inherited attributes.',
    kbLink: KbArticleUrls.groupInheritedUserAttributes,
  },

  data: () => ({
    attributesType,
  }),

  computed: {
    ...mapGetters(userGroupModel, [
      'getBooleanUserAttributes',
      'getNumberUserAttributes',
      'getObjectUserAttributes',
      'getStringUserAttributes',
      'getUserAttributes',
    ]),
  },

  beforeCreate() {
    this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options);
  },

  beforeMount() {
    const jsonFormatValidation = (value) => {
      const types = ['string', 'boolean', 'number'];
      let isValid = true;
      try {
        const parsedJSON = JSON.parse(value);
        if (types.includes(typeof parsedJSON) || !parsedJSON) {
          isValid = false;
        }
      } catch {
        isValid = false;
      }

      return isValid;
    };

    const uniqueNameValidation = value => this.getUserAttributes
      .filter(userAttribute => userAttribute.name === value).length === 1;

    this.$validator.extend('jsonFormatValidation', {
      getMessage: 'JSON formatted incorrectly',
      validate: jsonFormatValidation,
    });

    this.$validator.extend('reservedNameValidation', {
      getMessage: 'The attribute name is reserved',
      validate: value => !reservedGroupAttributes.includes(value),
    });

    this.$validator.extend('uniqueNameValidation', {
      getMessage: 'Attribute names must be unique',
      validate: uniqueNameValidation,
    });
  },

  mounted() {
    this.handleMounted();
  },

  methods: {
    ...mapActions(userGroupModel, [
      'addAttribute',
      'removeAttribute',
      'setPreviousItemField',
      'updateAttribute',
    ]),

    handleMounted() {
      if (!this.item.attributes) return;

      const attributes = cloneDeep(this.item.attributes);

      delete attributes.sudo;
      delete attributes.posixGroups;
      delete attributes.ldapGroups;
      delete attributes.radius;
      delete attributes.sambaEnabled;

      Object.entries(attributes).forEach(([name, value]) => {
        const type = typeof value;
        this.addAttribute({ attributeType: attributesType[type], name, value });
      });

      this.setPreviousItemField({ key: 'userAttributes', value: cloneDeep(this.item.userAttributes) });
    },

    /**
     * The property used to find an attribute is "tempId" an unique id
     * instead of the index because the store "userAttributes" is now
     * a single array that supports different literal types which are
     * being filtered hence they will have the same index and have a different index
     * from the store "userAttributes", so that meant to use a different way to find
     * an user attribute, however, the order of the parameters can't be changed since
     * they are being obtained from the generic component <ListEditor>.
    */
    handleRemoveAttribute(index, { tempId }) {
      this.removeAttribute({ tempId });
    },

    handleUpdateAttribute(index, attribute, key) {
      this.updateAttribute({
        key,
        tempId: attribute.tempId,
        value: attribute[key],
      });
    },
  },
};
</script>

<style module>
@import '../../css/sections.css';
@import '../../components/FormValidation/css/formFields.css';

.dropdownContainer {
  width: 210px;
}

.dropdownContainer button,
.dropdownContainer:focus-within button,
.dropdownContainer button:hover {
  background-color: var(--jcPrimary);
}

.dropdownContainer button::after {
  border-top: 6px solid currentColor;
}

.dropdownContainer div:first-of-type {
  border: 0 !important;
}

.dropdownContainer ul:first-of-type {
  width: 210px;
}

.dropdownContainer.noAttributes {
  padding-top: 15px;
}

.sectionContent .kbArticle {
  margin-bottom: 18px !important;
}

.sectionRoot .sectionContent {
  padding: 20px 0 0 0;
}
</style>
